import { Diamond } from "@src/types/DiamondDetail";
import { ShareType } from "../types";
import { Collection } from "@src/types/CollectionListResponse";
import { ButtonType } from "@components/atoms/Button/types";
import { getButtonCategory, ShareVia, useShareContent } from "./utils";
import { Button } from "@components/atoms";
import { useTranslation } from "react-i18next";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";
import {
  EventName,
  EventType,
  getAnalyticsProductData,
} from "@utils/analytics";
import { PdpSections } from "@utils/customization";
import * as Clipboard from "expo-clipboard";

const ShareButton = (
  handleSubmit: any,
  selectedOption: string,
  shareType: ShareType,
  userInfo: any,
  favoriteIds: any,
  mItem: Diamond | undefined,
  stoneItemDetail: Diamond | undefined,
  bulkItemIds: (number | undefined)[] | undefined,
  isImageChecked: boolean,
  sortedImageItems: any[],
  isReportsChecked: boolean,
  sortedReports: any[],
  checkedItems: { images: any; reports: any; videos: any },
  sortedVideoItems: any[],
  priceChecked: boolean,
  collection: Collection | undefined,
  bulkShareChecked: { bulkImageShare: any; bulkReportsShare: any },
  itemCount: number | undefined,
  favoriteCount: number | undefined,
  shareableLink: any,
  shareItem: any,
  onClose: () => void,
  Share: any,
  trackButtonEvent: any,
  shareData: any,
  jcSelectedOption?: any,
  personalization?: any,
  fromReviewPage?: any,
  matchingPairItemDetail?: Diamond | undefined,
  toastBottomMargin?: number
) => {
  const { t } = useTranslation();
  const handleShare = (data) => {
    if (
      selectedOption !== ShareVia.EMAIL &&
      selectedOption !== ShareVia.NATIVE &&
      selectedOption !== ShareVia.LINK
    )
      return;
    const { shareContent } = useShareContent(onClose);
    const isSingleShare = shareType === ShareType.singleShare;
    const isBulkShare =
      shareType === ShareType.multiShare ||
      shareType === ShareType.collections ||
      shareType === ShareType.favorites;

    const getCommonPayload = () => ({
      priceMode: userInfo.user.price_mode,
      stock_item_id:
        shareType === ShareType.favorites
          ? favoriteIds
          : isSingleShare
          ? mItem?.id
          : bulkItemIds,
    });

    const getFilteredMedia = () =>
      [
        ...(isImageChecked
          ? sortedImageItems
              .filter((item) => checkedItems.images[item.order])
              .sort((a, b) => a.order - b.order)
          : []),
        ...(checkedItems.videos
          ? sortedVideoItems.sort((a, b) => a.order - b.order)
          : []),
        ...(isReportsChecked
          ? sortedReports
              .filter((item) => checkedItems.reports[item.order])
              .sort((a, b) => a.order - b.order)
          : []),
      ].map((item) => ({
        type: item.type,
        link: item.link,
        order: item.order,
        video_type: item.video_type,
        thumb: item.thumb,
      }));

    const media = getFilteredMedia().length > 0 ? getFilteredMedia() : "null";

    const getSingleSharePayload = () => ({
      email_type: "share_stock_item",
      type: mItem?.type,
      to: data.email,
      subject: `Check out this ${mItem?.type} from VDB`,
      user_comment: data.comments,
      extra_attributes: {
        price: priceChecked,
        media: media,
        ...(fromReviewPage && {
          jewelry_builder: {
            config: jcSelectedOption.current.option_value,
            personalization: personalization ?? {},
            request_type: PdpSections.JEWELRY_BUILDER,
            selected_jewelry_id: mItem?.id,
            selected_stock_item_ids: [
              stoneItemDetail?.id,
              ...(matchingPairItemDetail ? [matchingPairItemDetail.id] : []),
            ],
            selected_stock_type: stoneItemDetail?.type,
          },
        }),
      },
      ...((jcSelectedOption?.current?.option_value &&
        Object.keys(jcSelectedOption?.current?.option_value).length > 0) ||
      (personalization && Object.keys(personalization).length > 0)
        ? {
            additional_attributes: {
              ...(jcSelectedOption?.current?.option_value &&
                Object.keys(jcSelectedOption?.current?.option_value).length >
                  0 && {
                  config: jcSelectedOption?.current?.option_value,
                }),
              ...(personalization &&
                Object.keys(personalization).length > 0 && {
                  personalization,
                }),
            },
          }
        : {}),
    });

    const getBulkSharePayload = () => ({
      email_address: data.email,
      share_type:
        shareType === ShareType.multiShare || shareType === ShareType.favorites
          ? "mix_multiple"
          : "mix_collection",
      share_via: "email",
      stock_item_type: "mix",
      comment: data.comments,
      extra_attributes: {
        price: priceChecked,
        permission: {
          show_image: bulkShareChecked.bulkImageShare,
          show_video: checkedItems.videos,
          show_certs: bulkShareChecked.bulkReportsShare,
        },
      },
      ...((jcSelectedOption?.current?.option_value &&
        Object.keys(jcSelectedOption?.current?.option_value).length > 0) ||
      (personalization && Object.keys(personalization).length > 0)
        ? {
            additional_attributes: {
              ...(jcSelectedOption?.current?.option_value &&
                Object.keys(jcSelectedOption?.current?.option_value).length >
                  0 && {
                  config: jcSelectedOption?.current?.option_value,
                }),
              ...(personalization &&
                Object.keys(personalization).length > 0 && {
                  personalization,
                }),
            },
          }
        : {}),
      ...(shareType === ShareType.collections && { id: collection?.id }),
    });

    const getNativeSharePayload = () => ({
      ...((shareType === ShareType.multiShare ||
        shareType === ShareType.collections) && {
        collectionLength:
          shareType === ShareType.multiShare
            ? itemCount
            : collection?.count || favoriteCount,
      }),
      share_type: isSingleShare
        ? "native"
        : shareType === ShareType.multiShare ||
          shareType === ShareType.favorites
        ? "mix_multiple"
        : "mix_collection",
      comment: "",
      share_via: selectedOption === ShareVia.LINK ? "link" : "native",
      id: isSingleShare ? mItem?.id : collection?.id,
      stock_item_type: isSingleShare ? mItem?.type : "mix",
      ...(shareType === ShareType.collections && { name: collection?.name }),
      extra_attributes: {
        price: priceChecked,
        ...(isSingleShare && { media: media }),
        ...((isBulkShare || shareType === ShareType.collections) && {
          permission: {
            show_image: bulkShareChecked.bulkImageShare,
            show_video: checkedItems.videos,
            show_certs: bulkShareChecked.bulkReportsShare,
          },
        }),
        ...(fromReviewPage && {
          jewelry_builder: {
            config: jcSelectedOption.current.option_value,
            personalization: personalization ?? {},
            request_type: PdpSections.JEWELRY_BUILDER,
            selected_jewelry_id: mItem?.id,
            selected_stock_item_ids: [
              stoneItemDetail?.id,
              ...(matchingPairItemDetail ? [matchingPairItemDetail.id] : []),
            ],
            selected_stock_type: stoneItemDetail?.type,
          },
        }),
      },
      ...((jcSelectedOption?.current?.option_value &&
        Object.keys(jcSelectedOption?.current?.option_value).length > 0) ||
      (personalization && Object.keys(personalization).length > 0)
        ? {
            additional_attributes: {
              ...(jcSelectedOption?.current?.option_value &&
                Object.keys(jcSelectedOption?.current?.option_value).length >
                  0 && {
                  config: jcSelectedOption?.current?.option_value,
                }),
              ...(personalization &&
                Object.keys(personalization).length > 0 && {
                  personalization,
                }),
            },
          }
        : {}),
    });

    const payload = {
      ...getCommonPayload(),
      ...(selectedOption === ShareVia.NATIVE || selectedOption === ShareVia.LINK
        ? getNativeSharePayload()
        : isSingleShare
        ? getSingleSharePayload()
        : getBulkSharePayload()),
    };

    const handleSuccess = (message: string) => {
      showToast(
        undefined,
        t(message),
        ToastTypes.SUCCESS,
        true,
        true,
        toastBottomMargin
      );
      onClose();
    };

    const handleError = (message: string) => {
      showToast(
        undefined,
        t(message),
        ToastTypes.ERROR,
        true,
        true,
        toastBottomMargin
      );
      onClose();
    };

    const shareableViaEmail = isSingleShare ? shareItem : shareableLink;

    const emailMutate = {
      onSuccess: (onSuccessData: any) =>
        handleSuccess(
          onSuccessData.data.translation_key ?? onSuccessData.data.message
        ),
      onError: (onErrorData: any) =>
        handleError(
          onErrorData.data.translation_key ?? onErrorData.data.message
        ),
    };

    const shareViaNative = {
      onSuccess: async (responseData: any) => {
        if (selectedOption === ShareVia.LINK) {
          await Clipboard.setStringAsync(
            fromReviewPage ||
              shareType === ShareType.collections ||
              shareType === ShareType.favorites ||
              shareType === ShareType.multiShare
              ? responseData?.data?.link
              : responseData?.data?.title
          );
          showToast(
            t("global.shareMedia.text", "Success!"),
            t(
              "global.shareMedia.description",
              "Link has been copied to clipboard"
            ),
            ToastTypes.SUCCESS,
            true,
            true,
            toastBottomMargin
          );
          onClose();
        } else {
          shareContent(responseData, true, false, false, Share);
        }
      },
    };

    if (selectedOption === ShareVia.EMAIL) {
      trackButtonEvent(
        EventName.BUTTON_CLICK,
        EventType.SHARE_VIA_EMAIL,
        getAnalyticsProductData(mItem)
      );

      shareableViaEmail.mutate(payload, emailMutate);
    } else {
      trackButtonEvent(
        EventName.BUTTON_CLICK,
        EventType.SHARE_VIA_LINK,
        getAnalyticsProductData(mItem)
      );
      shareableLink.mutate(payload, shareViaNative);
    }
  };
  return (
    <Button
      testID="share.button.submit"
      dataTestId="share.button.submit"
      type={ButtonType.primary}
      category={getButtonCategory(mItem?.type)}
      title={
        selectedOption === ShareVia.LINK
          ? t("shareScreen.button.title.link", "Copy a Link to Clipboard")
          : t("shareScreen.button.title.text")
      }
      onPress={
        selectedOption === ShareVia.EMAIL
          ? handleSubmit(handleShare)
          : () => handleShare(shareData)
      }
      disabled={shareItem.isLoading || shareableLink.isLoading}
      loading={shareableLink.isLoading || shareItem.isLoading}
    />
  );
};

export default ShareButton;
